<template>
    <section class="flex flex-col gap-5">
        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <h2 class="title-subsection text-ink">{{ t("reflections.admin.tab.title") }}</h2>
                <BaseButton theme="neutral" size="sm" class="ml-auto" @click="emit('settings')">
                    <i class="bi bi-gear" aria-hidden="true"></i>
                    {{ t("reflections.admin.tab.settings") }}
                </BaseButton>
                <BaseButton theme="primary" size="sm" :disabled="templates.length === 0" @click="emit('new-campaign', templates)">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    {{ t("reflections.admin.tab.newCampaignButton") }}
                </BaseButton>
                <BaseButton theme="neutral" size="sm" @click="emit('drafts')">
                    <i class="bi bi-pencil-square" aria-hidden="true"></i>
                    {{ t("reflections.admin.tab.drafts") }}
                    <BaseCountBadge v-if="draftCount > 0" :count="draftCount" />
                </BaseButton>
            </div>
            <p class="body-small text-ink-muted">{{ t("reflections.admin.tab.subtitle") }}</p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <div v-if="loading" class="body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />

        <template v-else>
            <AdminCompletionGlance v-model:state="completionState" :completion="completion" />

            <div class="flex flex-col gap-2">
                <div class="scaffold-details-small text-ink-muted">{{ t("reflectionsAdmin.hub.activeCampaigns") }}</div>
                <!-- A closed campaign has left this list, so its outcome leads it instead of sitting under its card. -->
                <BaseNotification
                    v-for="[campaignId, notice] in departedNotices"
                    :key="campaignId"
                    :theme="notice.theme"
                    :text="notice.text"
                    @dismiss="actionNotices.delete(campaignId)" />
                <BaseCard v-if="activeCampaigns.length === 0" padding="md" class="body-small text-ink-muted">
                    {{ t("reflections.admin.tab.noActiveCampaigns") }}
                </BaseCard>
                <template v-for="{ campaign, notice } in campaignRows" :key="campaign.id">
                    <AdminCampaignRow
                        :campaign="campaign"
                        :busy="pendingCampaignIds.has(campaign.id)"
                        :expanded="expandedCampaignId === campaign.id"
                        :hide-rater-status="hideRaterStatus"
                        @close="onClose"
                        @extend="onExtend"
                        @view="onView"
                        @toggle="onToggle" />
                    <!-- Right under the card the admin acted on, so a long page never hides why it did or didn't move. -->
                    <BaseNotification v-if="notice" :theme="notice.theme" :text="notice.text" @dismiss="actionNotices.delete(campaign.id)" />
                </template>
            </div>

            <AdminIndividual360sSection :hide-rater-status="hideRaterStatus" :report-template-id="individual360ReportTemplateId" @view-report="onViewIndividual360Report" />

            <AdminPreviousCyclesSection
                v-model:expanded-campaign-id="expandedCampaignId"
                :campaigns="closedCampaigns"
                :hide-rater-status="hideRaterStatus"
                class="mt-5"
                @view="onView" />
        </template>

        <AssessmentDemoTools :campaign-actions="campaignDemoActions" />
    </section>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type {
    AdminCampaignResponse,
    AdminCampaignSummary,
    AdminCompletionStateFilter,
    AdminCompletionSummary,
    AdminHubPayload,
    ExtendCampaignRequest,
    TemplateSummary,
} from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseCountBadge from "~vue/components/BaseCountBadge.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import AdminCampaignRow from "~vue/pages/reflections/AdminCampaignRow.vue";
import AdminCompletionGlance from "~vue/pages/reflections/AdminCompletionGlance.vue";
import { readAdminHubQuery } from "~vue/pages/reflections/adminHubQuery";
import AdminIndividual360sSection from "~vue/pages/reflections/AdminIndividual360sSection.vue";
import AdminPreviousCyclesSection from "~vue/pages/reflections/AdminPreviousCyclesSection.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

// The completion card owns its window and state and keeps both in the URL; the
// state is seeded here because the card's export filter is part of the hub's link.
const completionState = ref<AdminCompletionStateFilter>(readAdminHubQuery().state);
const campaigns = ref<AdminCampaignSummary[]>([]);
const templates = ref<TemplateSummary[]>([]);
const completion = ref<AdminCompletionSummary | null>(null);
const { individual360ReportTemplateId } = defineProps<{ individual360ReportTemplateId?: string }>();
const hideRaterStatus = ref(false);
// One campaign open at a time; the panel loads its rows lazily on expand.
const expandedCampaignId = ref<string | null>(null);
const loading = ref(true);
const loadError = ref<string | null>(null);
// Per campaign, so an action on one card can't re-enable another whose request is still in flight.
const pendingCampaignIds = reactive(new Set<string>());
type ActionNotice = { theme: "success" | "error"; text: string };
// One notice per campaign, so the outcome on one card never replaces another's.
const actionNotices = reactive(new Map<string, ActionNotice>());

const emit = defineEmits<{
    (event: "new-campaign", templates: TemplateSummary[]): void;
    (event: "view-campaign", campaignId: string): void;
    (event: "view-individual-360-report", templateId: string): void;
    (event: "settings"): void;
    (event: "drafts"): void;
}>();

// Drafts live on their own subpage; this list is what participants can see.
const activeCampaigns = computed(() => campaigns.value.filter((c) => c.state === "active"));
const campaignRows = computed(() => activeCampaigns.value.map((campaign) => ({ campaign, notice: actionNotices.get(campaign.id) ?? null })));
const departedNotices = computed(() => [...actionNotices].filter(([campaignId]) => !activeCampaigns.value.some((c) => c.id === campaignId)));
const draftCount = computed(() => campaigns.value.filter((c) => c.state === "draft").length);
const closedCampaigns = computed(() => campaigns.value.filter((c) => c.state === "closed"));
const campaignDemoActions = computed(() => activeCampaigns.value.map((campaign) => ({ id: campaign.id, label: campaign.name })));

type HubLoad = { ok: true } | { ok: false; message: string };

// Re-reads overlap when two cards are acted on back to back; a response that
// another re-read has since overtaken is dropped so it can't put back an older
// deadline or completion summary.
let hubLoadSequence = 0;

async function loadHub(): Promise<HubLoad> {
    const sequence = ++hubLoadSequence;
    const { data, error: fetchError } = await useFetch("/v1/reflections/admin/campaigns").json<AdminHubPayload>();
    if (sequence !== hubLoadSequence) {
        return { ok: true };
    }
    if (fetchError.value) {
        return { ok: false, message: fetchError.value.message || t("reflections.admin.tab.loadFailed") };
    }
    campaigns.value = data.value?.campaigns ?? [];
    templates.value = data.value?.templates ?? [];
    completion.value = data.value?.completion ?? null;
    hideRaterStatus.value = data.value?.hide_rater_status ?? false;
    return { ok: true };
}

onMounted(async () => {
    const result = await loadHub();
    if (!result.ok) {
        loadError.value = result.message;
    }
    loading.value = false;
});

function onView(campaignId: string) {
    emit("view-campaign", campaignId);
}

function onViewIndividual360Report(templateId: string) {
    emit("view-individual-360-report", templateId);
}

function onToggle(campaignId: string) {
    expandedCampaignId.value = expandedCampaignId.value === campaignId ? null : campaignId;
}

async function onExtend(campaign: AdminCampaignSummary, newClosesAt: string) {
    if (pendingCampaignIds.has(campaign.id)) {
        return;
    }
    const date = formatDate(newClosesAt, "short");
    if (!globalThis.confirm(t("reflections.admin.tab.extendConfirm", { name: campaign.name, date }))) {
        return;
    }
    pendingCampaignIds.add(campaign.id);
    actionNotices.delete(campaign.id);
    try {
        // The deadline this admin confirmed from goes with the request, so a stale
        // tab gets a 409 instead of stacking a second extension on another admin's.
        const body: ExtendCampaignRequest = { expected_closes_at: campaign.closes_at as string, extend_to: newClosesAt };
        const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${campaign.id}/extend`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        }).json<AdminCampaignResponse>();
        if (fetchError.value) {
            const code = (data.value as { code?: string; error?: string } | null)?.code;
            let text: string;
            if (code === "campaign_extension_limit_reached") {
                text = t("reflections.admin.tab.extendLimitReached", { name: campaign.name });
            } else if (code === "campaign_deadline_changed") {
                text = t("reflections.admin.tab.extendDeadlineChanged", { name: campaign.name });
            } else {
                text = (data.value as { error?: string } | null)?.error || t("reflections.admin.tab.extendFailed");
            }
            actionNotices.set(campaign.id, { theme: "error", text });
            const refreshed = await loadHub();
            if (!refreshed.ok && code === "campaign_deadline_changed") {
                // The notice promised a refreshed date; without one the card is stale and a retry repeats the conflict.
                actionNotices.set(campaign.id, { theme: "error", text: t("reflections.admin.tab.extendDeadlineChangedStale", { name: campaign.name }) });
            }
            return;
        }
        if (data.value) {
            const extended = (data.value as AdminCampaignResponse).campaign;
            campaigns.value = campaigns.value.map((existing) => (existing.id === campaign.id ? extended : existing));
            actionNotices.set(campaign.id, {
                theme: "success",
                text: t("reflections.admin.tab.extended", { name: campaign.name, date: formatDate(extended.closes_at ?? newClosesAt, "short") }),
            });
        }
        // The completion glance shows the earliest close date, so it is stale until re-read.
        const result = await loadHub();
        if (!result.ok) {
            actionNotices.set(campaign.id, { theme: "error", text: t("reflections.admin.tab.extendedButStale", { name: campaign.name }) });
        }
    } finally {
        // The card stays busy through the re-read, so a second extension can't
        // start while the first is still settling.
        pendingCampaignIds.delete(campaign.id);
    }
}

async function onClose(campaign: AdminCampaignSummary) {
    if (pendingCampaignIds.has(campaign.id)) {
        return;
    }
    if (!globalThis.confirm(t("reflections.admin.tab.closeConfirm", { name: campaign.name }))) {
        return;
    }
    pendingCampaignIds.add(campaign.id);
    actionNotices.delete(campaign.id);
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${campaign.id}/close`, { method: "POST" }).json<AdminCampaignResponse>();
        if (fetchError.value) {
            actionNotices.set(campaign.id, { theme: "error", text: (data.value as { error?: string } | null)?.error || t("reflections.admin.tab.closeFailed") });
            return;
        }
        if (data.value) {
            campaigns.value = campaigns.value.map((existing) => (existing.id === campaign.id ? (data.value as AdminCampaignResponse).campaign : existing));
        }
        actionNotices.set(campaign.id, { theme: "success", text: t("reflections.admin.tab.closed", { name: campaign.name }) });
        // Closing changes what is in flight, so the completion summary is stale
        // until it is re-read. Refetched without the loading flag so the panel
        // stays on screen instead of blanking to the spinner.
        const result = await loadHub();
        if (!result.ok) {
            actionNotices.set(campaign.id, { theme: "error", text: t("reflections.admin.tab.closedButStale", { name: campaign.name }) });
        }
    } finally {
        pendingCampaignIds.delete(campaign.id);
    }
}
</script>
